import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

const Courses = () => {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const q = query(collection(db, 'courses'), orderBy('courseName', 'asc'));
        const snapshot = await getDocs(q);
        const data = [];
        snapshot.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
        setCourses(data);
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    };
    loadCourses();
  }, []);

  const activeCourses = courses.filter(c => c.active).length;

  return (
    <div className="container courses-page">
      <motion.h1
        className="courses-page-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <i className="fas fa-graduation-cap" style={{ color: 'var(--clr-blue)' }}></i> Our Courses
      </motion.h1>
      <p className="courses-page-subtitle">Explore our comprehensive range of medical and healthcare programs</p>

      {/* Stats */}
      <div className="courses-stats">
        {[
          { value: courses.length, label: 'Total Courses', desc: 'Medical & Healthcare Programs', color: 'var(--clr-blue)', icon: 'fa-graduation-cap' },
          { value: '800+', label: 'Students Enrolled', desc: 'Currently pursuing degrees', color: 'var(--clr-emerald)', icon: 'fa-users' },
          { value: activeCourses, label: 'Active Courses', desc: 'Currently accepting applications', color: 'var(--clr-amber)', icon: 'fa-clock' },
          { value: '95%', label: 'Placement Rate', desc: 'Successful career outcomes', color: 'var(--clr-purple)', icon: 'fa-trophy' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            className="courses-stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            style={{ borderBottomColor: stat.color }}
          >
            <div className="courses-stat-icon" style={{ background: stat.color }}>
              <i className={`fas ${stat.icon}`}></i>
            </div>
            <div className="courses-stat-value" style={{ color: stat.color }}>{stat.value}</div>
            <div className="courses-stat-label">{stat.label}</div>
            <div className="courses-stat-desc">{stat.desc}</div>
          </motion.div>
        ))}
      </div>

      {/* Courses Grid */}
      <div className="courses-grid">
        {courses.length === 0 ? (
          <div className="courses-loading">
            <i className="fas fa-spinner fa-pulse"></i>
            <p>Loading courses...</p>
          </div>
        ) : (
          courses.map((course, index) => {
            const fee = course.courseFee ? parseInt(course.courseFee).toLocaleString('en-IN') : 'Contact';
            return (
              <motion.div
                key={course.id}
                className="course-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -10, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                <Link to={`/courses/${course.id}`} className="course-card-link">
                  <div className="course-card-banner">
                    <i className={`fas ${course.icon || 'fa-graduation-cap'}`}></i>
                    <div className={`course-card-status ${course.active ? 'active' : 'inactive'}`}>
                      {course.active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="course-card-content">
                    <h3>{course.courseName || 'Course Name'}</h3>
                    <p>{(course.description || '').substring(0, 120)}...</p>
                    <div className="course-card-details">
                      <div>
                        <i className="fas fa-clock" style={{ color: 'var(--clr-blue)' }}></i>
                        <div>{course.duration || 'N/A'}</div>
                        <small>Duration</small>
                      </div>
                      <div>
                        <i className="fas fa-rupee-sign" style={{ color: 'var(--clr-emerald)' }}></i>
                        <div>₹{fee}</div>
                        <small>Total Fee</small>
                      </div>
                    </div>
                    <div className="course-card-btn">
                      View Details →
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Add CSS for responsiveness */}
      <style>{`
        /* Courses Page Styles */
        .courses-page {
          padding-top: 40px;
        }

        .courses-page-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          margin-bottom: 10px;
        }

        .courses-page-subtitle {
          color: var(--text-secondary);
          margin-bottom: 30px;
        }

        /* Stats */
        .courses-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .courses-stat-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          border-bottom: 4px solid var(--clr-blue);
          transition: all 0.3s ease;
        }

        .courses-stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
        }

        .courses-stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
          font-size: 1.5rem;
          color: white;
        }

        .courses-stat-value {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          font-weight: 800;
        }

        .courses-stat-label {
          font-size: 0.85rem;
          font-weight: 700;
          margin: 8px 0 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .courses-stat-desc {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        /* Courses Grid */
        .courses-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }

        .course-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid var(--clr-blue-border);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .course-card-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .course-card-banner {
          position: relative;
          height: 200px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .course-card-banner i {
          font-size: 3.5rem;
          color: white;
          filter: drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.2));
          z-index: 1;
        }

        .course-card-status {
          position: absolute;
          top: 15px;
          right: 15px;
          padding: 5px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 700;
          color: white;
          z-index: 2;
        }

        .course-card-status.active {
          background: rgba(0, 0, 0, 0.6);
        }

        .course-card-status.inactive {
          background: rgba(220, 38, 38, 0.8);
        }

        .course-card-content {
          padding: 24px;
        }

        .course-card-content h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.4rem;
          margin-bottom: 10px;
        }

        .course-card-content p {
          color: var(--text-secondary);
          font-size: 0.85rem;
          line-height: 1.6;
          margin-bottom: 20px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .course-card-details {
          display: flex;
          justify-content: space-between;
          padding: 15px;
          background: rgba(255, 255, 255, 0.4);
          border-radius: 14px;
          margin-bottom: 20px;
        }

        .course-card-details > div {
          text-align: center;
          flex: 1;
        }

        .course-card-details > div i {
          font-size: 1.2rem;
        }

        .course-card-details > div div {
          font-size: 0.85rem;
          font-weight: 700;
          margin-top: 6px;
        }

        .course-card-details > div small {
          font-size: 0.65rem;
          color: var(--text-muted);
          display: block;
        }

        .course-card-btn {
          display: block;
          text-align: center;
          padding: 12px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          border-radius: 40px;
          color: white;
          font-weight: 700;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }

        .course-card:hover .course-card-btn {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(14, 165, 233, 0.3);
        }

        .courses-loading {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 24px;
        }

        .courses-loading i {
          font-size: 2rem;
          display: block;
          margin-bottom: 10px;
          color: var(--clr-blue);
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .courses-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .courses-grid {
            grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
            gap: 24px;
          }
        }

        @media (max-width: 768px) {
          .courses-page {
            padding-top: 20px;
          }

          .courses-page-title {
            font-size: 1.8rem;
          }

          .courses-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 30px;
          }

          .courses-stat-card {
            padding: 16px 12px;
          }

          .courses-stat-icon {
            width: 50px;
            height: 50px;
            font-size: 1.2rem;
          }

          .courses-stat-value {
            font-size: 2rem;
          }

          .courses-stat-label {
            font-size: 0.75rem;
          }

          .courses-stat-desc {
            font-size: 0.65rem;
          }

          .courses-grid {
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 40px;
          }

          .course-card-banner {
            height: 160px;
          }

          .course-card-banner i {
            font-size: 2.5rem;
          }

          .course-card-content {
            padding: 16px;
          }

          .course-card-content h3 {
            font-size: 1.1rem;
          }

          .course-card-content p {
            font-size: 0.8rem;
            -webkit-line-clamp: 2;
            margin-bottom: 15px;
          }

          .course-card-details {
            padding: 12px;
            margin-bottom: 15px;
          }

          .course-card-details > div div {
            font-size: 0.75rem;
          }

          .course-card-details > div small {
            font-size: 0.55rem;
          }

          .course-card-btn {
            padding: 10px;
            font-size: 0.8rem;
          }

          .course-card-status {
            font-size: 0.6rem;
            padding: 4px 10px;
          }
        }

        @media (max-width: 480px) {
          .courses-page-title {
            font-size: 1.5rem;
          }

          .courses-page-subtitle {
            font-size: 0.9rem;
            margin-bottom: 20px;
          }

          .courses-stats {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 25px;
          }

          .courses-stat-card {
            padding: 14px 10px;
          }

          .courses-stat-icon {
            width: 42px;
            height: 42px;
            font-size: 1rem;
          }

          .courses-stat-value {
            font-size: 1.5rem;
          }

          .courses-stat-label {
            font-size: 0.65rem;
            margin: 5px 0 3px;
          }

          .courses-stat-desc {
            font-size: 0.55rem;
          }

          .courses-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .course-card-banner {
            height: 150px;
          }

          .course-card-banner i {
            font-size: 2.2rem;
          }

          .course-card-content {
            padding: 14px;
          }

          .course-card-content h3 {
            font-size: 1rem;
          }

          .course-card-content p {
            font-size: 0.75rem;
            -webkit-line-clamp: 2;
            margin-bottom: 12px;
          }

          .course-card-details {
            padding: 10px;
            margin-bottom: 12px;
          }

          .course-card-details > div div {
            font-size: 0.7rem;
          }

          .course-card-details > div small {
            font-size: 0.5rem;
          }

          .course-card-btn {
            padding: 10px;
            font-size: 0.75rem;
          }

          .course-card-status {
            font-size: 0.55rem;
            padding: 3px 8px;
            top: 10px;
            right: 10px;
          }

          .courses-loading {
            padding: 40px 20px;
          }

          .courses-loading i {
            font-size: 1.5rem;
          }
        }

        @media (max-width: 360px) {
          .courses-stats {
            grid-template-columns: 1fr;
          }

          .courses-stat-card {
            padding: 12px;
          }

          .course-card-banner {
            height: 130px;
          }

          .course-card-banner i {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Courses;